import {
  faFacebookSquare,
  faInstagram,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
// @fortawesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
// component styles
import customFooterStyle from 'assets/jss/material-kit-pro-react/components/customFooterStyle.jsx';
// nodejs library that concatenates classes
import classNames from 'classnames';
// core components
import Button from 'components/CustomButtons/Button.jsx';
import Footer from 'components/Footer/Footer.jsx';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
// gatsby libraries
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';

import ContactForm from '../ContactForm/ContactForm';
import PrivacyTerms from '../PrivacyTerms/PrivacyTerms';

const useStyles = makeStyles(customFooterStyle);

export default function CustomFooter() {
  const classes = useStyles();
  const {
    chilimaniaLogo,
    countryEdgeLogo,
    turtleneck,
  } = useStaticQuery(graphql`
    query getFooterLogos {
      chilimaniaLogo: file(
        relativePath: { eq: "logos/chilimania-logo-transparent.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      countryEdgeLogo: file(
        relativePath: { eq: "logos/country-edge-logo.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      turtleneck: file(relativePath: { eq: "logos/turtleneck.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);
  return (
    <div>
      <Footer
        theme="white"
        content={
          <GridContainer
            className={classNames(classes.copyright, classes.alignCenter)}
          >
            <GridItem xs={12} sm={4}>
              Copyright &copy; {1900 + new Date().getYear()}
            </GridItem>
            <GridItem xs={12} sm={4}>
              <div className={classes.turtleneckWrapper}>
                <GatsbyImage
                  image={turtleneck.childImageSharp.gatsbyImageData}
                  className={classes.turtleneckLogo}
                  alt="Turtleneck Software Logo"
                  imgStyle={{ objectFit: 'contain' }}
                />
                Turtleneck Software
              </div>
              <div className={classes.privacyModalWrapper}>
                <PrivacyTerms />
              </div>
            </GridItem>
            <GridItem xs={12} sm={4}>
              All Rights Reserved.
            </GridItem>
          </GridContainer>
        }
      >
        <div className={classes.footer}>
          <GridContainer>
            <GridItem xs={12} sm={6} md={4}>
              <h5 className={classes.aboutTitle}>About Chilimania</h5>
              <div className={classes.aboutText}>
                Chilimania is all about supporting our youth and community.
                We’re home to the Wisconsin State Championship CASI-sanctioned
                chili cook-off, a traditional chili cook-off, a salsa contest,
                public chili tasting, 12 hours of live music, food, fun, and
                more. Since 1990, the festival has been held annually on the
                Saturday after Labor Day in beautiful downtown, Edgerton,
                Wisconsin.
              </div>
            </GridItem>
            <GridItem xs={12} sm={6} md={4}>
              <div className={classes.logoContainer}>
                <div className={classes.logoWrapper}>
                  <GatsbyImage
                    image={chilimaniaLogo.childImageSharp.gatsbyImageData}
                    className={classes.footerLogo}
                    alt="Chilimania Logo"
                    imgStyle={{ objectFit: 'contain' }}
                  />
                </div>
                <div className={classes.logoWrapper}>
                  <GatsbyImage
                    image={countryEdgeLogo.childImageSharp.gatsbyImageData}
                    className={classes.footerLogo}
                    alt="Country Edge Logo"
                    imgStyle={{ objectFit: 'contain' }}
                  />
                </div>
              </div>
            </GridItem>
            <GridItem xs={12} sm={12} md={4} className={classes.socialIconItem}>
              <h5 className={classes.footerContact}>
                <ContactForm />
              </h5>
              <h5 className={classes.followTitle}>Follow Us</h5>
              <ul className={classes.socialButtons}>
                <li>
                  <Button
                    justIcon
                    simple
                    href="https://www.facebook.com/Chilimania"
                    color="facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Chilimania Facebook page (opens in new tab)"
                  >
                    <FontAwesomeIcon icon={faFacebookSquare} />
                  </Button>
                </li>
                <li>
                  <Button
                    justIcon
                    simple
                    href="https://www.youtube.com/channel/UCHgU74XfEPwfcFABim72Pww"
                    color="youtube"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Chilimania YouTube channel (opens in new tab)"
                  >
                    <FontAwesomeIcon icon={faYoutube} />
                  </Button>
                </li>
                <li>
                  <Button
                    justIcon
                    simple
                    href="https://www.instagram.com/chilimaniawi/"
                    color="instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Chilimania Instagram (opens in new tab)"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </Button>
                </li>
                <li>
                  <Button
                    justIcon
                    simple
                    href="https://www.tiktok.com/discover/chilimania-edgerton"
                    color="github"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit Chilimania TikTok (opens in new tab)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                    >
                      <path
                        fill="rgb(0, 0, 0)"
                        d="M544.5 273.9C500.5 274 457.5 260.3 421.7 234.7L421.7 413.4C421.7 446.5 411.6 478.8 392.7 506C373.8 533.2 347.1 554 316.1 565.6C285.1 577.2 251.3 579.1 219.2 570.9C187.1 562.7 158.3 545 136.5 520.1C114.7 495.2 101.2 464.1 97.5 431.2C93.8 398.3 100.4 365.1 116.1 336C131.8 306.9 156.1 283.3 185.7 268.3C215.3 253.3 248.6 247.8 281.4 252.3L281.4 342.2C266.4 337.5 250.3 337.6 235.4 342.6C220.5 347.6 207.5 357.2 198.4 369.9C189.3 382.6 184.4 398 184.5 413.8C184.6 429.6 189.7 444.8 199 457.5C208.3 470.2 221.4 479.6 236.4 484.4C251.4 489.2 267.5 489.2 282.4 484.3C297.3 479.4 310.4 469.9 319.6 457.2C328.8 444.5 333.8 429.1 333.8 413.4L333.8 64L421.8 64C421.7 71.4 422.4 78.9 423.7 86.2C426.8 102.5 433.1 118.1 442.4 131.9C451.7 145.7 463.7 157.5 477.6 166.5C497.5 179.6 520.8 186.6 544.6 186.6L544.6 274z"
                      />
                    </svg>
                  </Button>
                </li>
              </ul>
              <h5>Join Us</h5>
              <h4>
                6,000 <small>Anual Attendees</small>
              </h4>
              <h4>
                $200,000+ <small>Raised for Charity</small>
              </h4>
            </GridItem>
          </GridContainer>
        </div>
      </Footer>
    </div>
  );
}
